import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Plus, Loader2, Trash2, Pencil, Save, X, Eye, EyeOff } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const ROLES = ["Standard Animator", "High-Quality Animator", "Script Writer", "Video Editor"];
const CATS = [
  { id: "style", label: "Style & quality references" },
  { id: "channel", label: "Channel examples · content direction" },
];
const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-2 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
const EMPTY = { title: "", url: "", note: "", order: 0, enabled: true };

// Owner-managed hiring references — the public role modals read these automatically.
export const HiringRefsCard = () => {
  const [role, setRole] = useState(ROLES[0]);
  const [refs, setRefs] = useState(null);
  const [adding, setAdding] = useState(null); // category id or null
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/hiring-references`, { headers: authHeaders() })
      .then((r) => setRefs(r.data))
      .catch(() => toast.error("Could not load hiring references."));
  }, []);
  useEffect(load, [load]);

  const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

  const add = async (category) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/hiring-references`, { role, category, ...form, order: Number(form.order) || 0 }, { headers: authHeaders() });
      toast.success("Reference added — live on the public role modal now.");
      setAdding(null);
      setForm(EMPTY);
      load();
    } catch (e) { err(e, "Could not add reference."); }
    finally { setBusy(false); }
  };

  const save = async (id) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/hiring-references/${id}`, { ...editForm, order: Number(editForm.order) || 0 }, { headers: authHeaders() });
      toast.success("Reference updated.");
      setEditId(null);
      load();
    } catch (e) { err(e, "Could not save."); }
    finally { setBusy(false); }
  };

  const toggle = async (ref) => {
    await axios.patch(`${API}/admin/hiring-references/${ref.id}`, { enabled: !ref.enabled }, { headers: authHeaders() }).catch((e) => err(e, "Toggle failed."));
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this reference from the public role modal?")) return;
    await axios.delete(`${API}/admin/hiring-references/${id}`, { headers: authHeaders() }).catch((e) => err(e, "Delete failed."));
    load();
  };

  const roleRefs = (refs || []).filter((r) => r.role === role);

  return (
    <div data-testid="hiring-refs-card" className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
      <div className="flex items-center gap-2.5">
        <Link2 className="h-5 w-5 text-cyan-400" />
        <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Hiring references</h2>
      </div>
      <p className="mt-2 text-xs text-[var(--pv-text-4)]">
        The style/quality references and channel examples shown inside each public role modal on the landing page.
        Each role is configured separately — Standard and HQ can use completely different references.
      </p>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {ROLES.map((r) => (
          <button key={r} data-testid={`hrefs-role-${r.toLowerCase().replace(/[\s/-]+/g, "-")}`} onClick={() => { setRole(r); setAdding(null); setEditId(null); }}
            className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold transition-colors ${role === r ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)]"}`}>
            {r}
          </button>
        ))}
      </div>

      {!refs ? (
        <p className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : (
        CATS.map((cat) => {
          const items = roleRefs.filter((r) => r.category === cat.id);
          return (
            <div key={cat.id} data-testid={`hrefs-group-${cat.id}`} className="mt-6">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{cat.label} ({items.length})</p>
                <button data-testid={`hrefs-add-${cat.id}`} onClick={() => { setAdding(adding === cat.id ? null : cat.id); setForm(EMPTY); }} className="flex items-center gap-1 rounded-full bg-cyan-500/10 border border-cyan-500/25 px-3 py-1.5 text-[11px] font-bold text-cyan-300 hover:bg-cyan-500/20">
                  <Plus className="h-3 w-3" /> Add
                </button>
              </div>

              {adding === cat.id && (
                <div data-testid={`hrefs-add-form-${cat.id}`} className="mt-3 grid gap-2 rounded-2xl border border-cyan-500/20 bg-[var(--pv-subtle)] p-4 sm:grid-cols-2">
                  <input data-testid="hrefs-add-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Display title" className={inputCls} />
                  <input data-testid="hrefs-add-url" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://…" className={inputCls} />
                  <input data-testid="hrefs-add-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Optional note" className={inputCls} />
                  <input data-testid="hrefs-add-order" type="number" min="0" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} placeholder="Order (0 = first)" className={inputCls} />
                  <button data-testid="hrefs-add-save" onClick={() => add(cat.id)} disabled={busy || form.title.length < 2 || form.url.length < 5} className="flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Save reference
                  </button>
                </div>
              )}

              <div className="mt-3 space-y-2">
                {items.length === 0 && adding !== cat.id && (
                  <p data-testid={`hrefs-empty-${cat.id}`} className="rounded-2xl border border-dashed border-[var(--pv-border-strong)] px-4 py-4 text-center text-xs text-[var(--pv-text-4)]">
                    None configured — this section is hidden on the public modal for {role}.
                  </p>
                )}
                {items.map((ref) => (
                  <div key={ref.id} data-testid={`hrefs-row-${ref.id}`} className={`rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-3 ${ref.enabled ? "" : "opacity-50"}`}>
                    {editId === ref.id ? (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <input data-testid="hrefs-edit-title" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} />
                        <input data-testid="hrefs-edit-url" value={editForm.url} onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))} className={inputCls} />
                        <input data-testid="hrefs-edit-note" value={editForm.note} onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))} placeholder="Optional note" className={inputCls} />
                        <input data-testid="hrefs-edit-order" type="number" min="0" value={editForm.order} onChange={(e) => setEditForm((f) => ({ ...f, order: e.target.value }))} className={inputCls} />
                        <div className="flex gap-2">
                          <button data-testid="hrefs-edit-save" onClick={() => save(ref.id)} disabled={busy} className="flex items-center gap-1 rounded-full bg-cyan-500 px-4 py-1.5 text-[11px] font-bold text-white hover:bg-cyan-600 disabled:opacity-50"><Save className="h-3 w-3" /> Save</button>
                          <button onClick={() => setEditId(null)} className="rounded-full px-3 py-1.5 text-[11px] font-semibold text-[var(--pv-text-4)]"><X className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-[var(--pv-text)]">{ref.title}</span>
                          <a href={ref.url} target="_blank" rel="noreferrer" className="block truncate text-[11px] text-cyan-400 hover:text-cyan-300">{ref.url}</a>
                          {ref.note && <span className="block truncate text-[11px] text-[var(--pv-text-4)]">{ref.note}</span>}
                        </span>
                        <span className="rounded-full border border-[var(--pv-border)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-4)]">#{ref.order}</span>
                        <button data-testid={`hrefs-toggle-${ref.id}`} onClick={() => toggle(ref)} title={ref.enabled ? "Disable (hide publicly)" : "Enable"} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:text-cyan-400">
                          {ref.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                        <button data-testid={`hrefs-edit-${ref.id}`} onClick={() => { setEditId(ref.id); setEditForm({ title: ref.title, url: ref.url, note: ref.note || "", order: ref.order, enabled: ref.enabled }); }} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:text-cyan-400"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`hrefs-delete-${ref.id}`} onClick={() => remove(ref.id)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};
